import React from "react";
import { InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function lcm(a, b) {
  return Math.abs(a * b) / gcd(a, b);
}

function lcmMany(numbers) {
  return numbers.reduce((acc, item) => lcm(acc, item), 1);
}

function properNumerators(denominator, min = 1, max = denominator - 1) {
  return Array.from({ length: max - min + 1 }, (_, index) => min + index).filter(
    (numerator) => numerator > 0 && numerator < denominator && gcd(numerator, denominator) === 1
  );
}

function randomProperFraction(denominator, minNumerator = 1, maxNumerator = denominator - 1) {
  const candidates = properNumerators(denominator, minNumerator, maxNumerator);
  return {
    numerator: pick(candidates.length ? candidates : properNumerators(denominator)),
    denominator,
  };
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function addFractions(a, b) {
  return normalizeFraction(a.numerator * b.denominator + b.numerator * a.denominator, a.denominator * b.denominator);
}

function subtractFractions(a, b) {
  return normalizeFraction(a.numerator * b.denominator - b.numerator * a.denominator, a.denominator * b.denominator);
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  if (numerator < 0) return `-\\frac{${Math.abs(numerator)}}{${denominator}}`;
  return `\\frac{${numerator}}{${denominator}}`;
}

function fractionValueLatex(fraction) {
  return fractionLatex(fraction.numerator, fraction.denominator);
}

function mixedLatex({ whole, numerator, denominator }) {
  if (!numerator) return `${whole}`;
  const sign = whole < 0 ? "-" : "";
  return `${sign}${Math.abs(whole)}\\frac{${numerator}}{${denominator}}`;
}

function fractionToMixed(fraction) {
  const sign = fraction.numerator < 0 ? -1 : 1;
  const absNumerator = Math.abs(fraction.numerator);
  const whole = Math.floor(absNumerator / fraction.denominator) * sign;
  const rest = absNumerator % fraction.denominator;
  const simpleRest = normalizeFraction(rest, fraction.denominator);
  return { whole, numerator: simpleRest.numerator, denominator: simpleRest.denominator };
}

function displayFractionLatex(fraction) {
  return fraction.display === "mixed" ? mixedLatex(fractionToMixed(fraction)) : fractionValueLatex(fraction);
}

function answerLatex(fraction) {
  if (Math.abs(fraction.numerator) > fraction.denominator && fraction.denominator !== 1) {
    return mixedLatex(fractionToMixed(fraction));
  }
  return fractionValueLatex(fraction);
}

function MathText({ math, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={math} />
    </span>
  );
}

function expressionLatex(terms, useDisplay = true) {
  return terms
    .map((term, index) => {
      const body = useDisplay ? displayFractionLatex(term.fraction) : fractionValueLatex(term.fraction);
      if (index === 0) return body;
      return `${term.op === "+" ? "+" : "-"}${body}`;
    })
    .join("");
}

function makeMixedFraction(whole = randInt(1, 5), denominator = randInt(3, 9)) {
  const rest = randomProperFraction(denominator);
  return {
    numerator: whole * denominator + rest.numerator,
    denominator,
    display: "mixed",
  };
}

function parseRawFraction(raw) {
  if (typeof raw !== "string") return null;
  const compact = raw.trim().replace(",", ".").replace(/[−–]/g, "-").replace(/\s+/g, "");
  const mixed = raw.trim().replace(",", ".").replace(/[−–]/g, "-").replace(/\s+/g, " ").match(/^(-?\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const numerator = Number(mixed[2]);
    const denominator = Number(mixed[3]);
    if (!Number.isInteger(whole) || !Number.isInteger(numerator) || !Number.isInteger(denominator)) return null;
    if (denominator === 0 || numerator <= 0 || numerator >= denominator) return null;
    const sign = whole < 0 ? -1 : 1;
    return normalizeFraction(sign * (Math.abs(whole) * denominator + numerator), denominator);
  }
  const cleaned = compact;
  const match = cleaned.match(/^(-?\d+)\/(-?\d+)$/);
  if (match) {
    const numerator = Number(match[1]);
    const denominator = Number(match[2]);
    if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator === 0) return null;
    return normalizeFraction(numerator, denominator);
  }
  if (/^-?\d+$/.test(cleaned)) return { numerator: Number(cleaned), denominator: 1 };
  return null;
}

function calculateResult(terms) {
  return terms.slice(1).reduce((acc, term) => {
    return term.op === "+" ? addFractions(acc, term.fraction) : subtractFractions(acc, term.fraction);
  }, terms[0].fraction);
}

function makeTask(type, terms, meta = {}) {
  return { type, data: { terms, result: calculateResult(terms), ...meta } };
}

function sameDenominatorAdditionTask() {
  const denominator = randInt(5, 12);
  let first;
  let second;
  do {
    first = randomProperFraction(denominator);
    second = randomProperFraction(denominator);
  } while (addFractions(first, second).numerator >= addFractions(first, second).denominator);
  return makeTask("FractionAddSameDenominatorTask", [
    { op: "+", fraction: first },
    { op: "+", fraction: second },
  ]);
}

function sameDenominatorSubtractionTask() {
  const denominator = randInt(5, 12);
  let first;
  let second;
  do {
    first = randomProperFraction(denominator);
    second = randomProperFraction(denominator);
  } while (subtractFractions(first, second).numerator <= 0);
  return makeTask("FractionSubtractSameDenominatorTask", [
    { op: "+", fraction: first },
    { op: "-", fraction: second },
  ]);
}

function relatedDenominatorAdditionTask() {
  const pairs = [[2, 4], [3, 6], [4, 8], [5, 10], [3, 9], [4, 12]];
  let first;
  let second;
  do {
    const [base, larger] = pick(pairs);
    first = randomProperFraction(base);
    second = randomProperFraction(larger);
  } while (addFractions(first, second).numerator >= addFractions(first, second).denominator);
  return makeTask("FractionAddRelatedDenominatorTask", [
    { op: "+", fraction: first },
    { op: "+", fraction: second },
  ]);
}

function relatedDenominatorSubtractionTask() {
  const pairs = [[2, 4], [3, 6], [4, 8], [5, 10], [3, 9], [4, 12]];
  let first;
  let second;
  do {
    const [base, larger] = pick(pairs);
    first = randomProperFraction(base);
    second = randomProperFraction(larger);
  } while (subtractFractions(first, second).numerator <= 0);
  return makeTask("FractionSubtractRelatedDenominatorTask", [
    { op: "+", fraction: first },
    { op: "-", fraction: second },
  ]);
}

function unrelatedDenominatorPair(maxDenominator = 12) {
  const pairs = [];
  for (let a = 3; a <= maxDenominator; a += 1) {
    for (let b = a + 1; b <= maxDenominator; b += 1) {
      const common = lcm(a, b);
      if (b % a !== 0 && common <= 60) pairs.push([a, b]);
    }
  }
  return pick(pairs);
}

function unrelatedDenominatorAdditionTask() {
  let first;
  let second;
  do {
    const [d1, d2] = unrelatedDenominatorPair();
    first = randomProperFraction(d1);
    second = randomProperFraction(d2);
  } while (addFractions(first, second).numerator >= addFractions(first, second).denominator);
  return makeTask("FractionAddCommonDenominatorTask", [
    { op: "+", fraction: first },
    { op: "+", fraction: second },
  ]);
}

function unrelatedDenominatorSubtractionTask() {
  let first;
  let second;
  do {
    const [d1, d2] = unrelatedDenominatorPair();
    first = randomProperFraction(d1);
    second = randomProperFraction(d2);
  } while (subtractFractions(first, second).numerator <= 0);
  return makeTask("FractionSubtractCommonDenominatorTask", [
    { op: "+", fraction: first },
    { op: "-", fraction: second },
  ]);
}

function threeFractionTask() {
  const denominator = pick([6, 8, 10, 12]);
  let terms;
  do {
    terms = [
      { op: "+", fraction: randomProperFraction(denominator) },
      { op: "+", fraction: randomProperFraction(denominator) },
      { op: "-", fraction: randomProperFraction(denominator) },
    ];
  } while (calculateResult(terms).numerator <= 0 || calculateResult(terms).numerator >= calculateResult(terms).denominator);
  return makeTask("FractionThreeTermsTask", terms);
}

function mixedThreeFractionTask() {
  const denominatorSets = [[2, 4, 6], [2, 4, 8], [3, 6, 9], [3, 6, 12], [4, 6, 12], [5, 10, 15], [6, 9, 18], [9, 12, 36]];
  let terms;
  do {
    const denominators = pick(denominatorSets);
    terms = [
      { op: "+", fraction: randomProperFraction(denominators[0]) },
      { op: "+", fraction: randomProperFraction(denominators[1]) },
      { op: "-", fraction: randomProperFraction(denominators[2]) },
    ];
  } while (
    calculateResult(terms).numerator <= 0 ||
    calculateResult(terms).numerator >= calculateResult(terms).denominator ||
    lcmMany(terms.map((term) => term.fraction.denominator)) > 36
  );
  return makeTask("FractionMixedThreeTermsTask", terms);
}

function negativeResultTask() {
  let first;
  let second;
  do {
    const [d1, d2] = pick([[2, 4], [3, 4], [3, 5], [4, 6], [5, 8]]);
    first = randomProperFraction(d1);
    second = randomProperFraction(d2);
  } while (subtractFractions(first, second).numerator >= 0);
  return makeTask("FractionNegativeResultTask", [
    { op: "+", fraction: first },
    { op: "-", fraction: second },
  ]);
}

function mixedPlusProperTask() {
  return makeTask("FractionMixedPlusProperTask", [
    { op: "+", fraction: makeMixedFraction(randInt(1, 5), randInt(3, 9)) },
    { op: "+", fraction: randomProperFraction(randInt(3, 10)) },
  ]);
}

function mixedMinusProperTask() {
  let terms;
  do {
    terms = [
      { op: "+", fraction: makeMixedFraction(randInt(1, 6), randInt(3, 9)) },
      { op: "-", fraction: randomProperFraction(randInt(3, 10)) },
    ];
  } while (calculateResult(terms).numerator <= 0);
  return makeTask("FractionMixedMinusProperTask", terms);
}

function mixedPlusMixedTask() {
  return makeTask("FractionMixedPlusMixedTask", [
    { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 9)) },
    { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 9)) },
  ]);
}

function mixedMinusMixedTask() {
  let terms;
  do {
    terms = [
      { op: "+", fraction: makeMixedFraction(randInt(3, 7), randInt(3, 9)) },
      { op: "-", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 9)) },
    ];
  } while (calculateResult(terms).numerator <= 0);
  return makeTask("FractionMixedMinusMixedTask", terms);
}

function mixedThreeTermsTask() {
  let terms;
  do {
    terms = [
      { op: "+", fraction: makeMixedFraction(randInt(1, 4), randInt(3, 8)) },
      { op: "+", fraction: randomProperFraction(randInt(3, 9)) },
      { op: "-", fraction: makeMixedFraction(randInt(1, 3), randInt(3, 8)) },
    ];
  } while (calculateResult(terms).numerator <= 0);
  return makeTask("FractionMixedThreeExpressionTask", terms);
}

export function generateTasks() {
  return [
    sameDenominatorAdditionTask(),
    sameDenominatorSubtractionTask(),
    threeFractionTask(),
    relatedDenominatorAdditionTask(),
    relatedDenominatorSubtractionTask(),
    unrelatedDenominatorAdditionTask(),
    unrelatedDenominatorSubtractionTask(),
    mixedThreeFractionTask(),
    negativeResultTask(),
    mixedPlusProperTask(),
    mixedMinusProperTask(),
    mixedPlusMixedTask(),
    mixedMinusMixedTask(),
    mixedThreeTermsTask(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <MathText math={expressionLatex(data.terms)} />.
    </p>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label="wynik" placeholder="np. 3/8" />;
}

export function checkAnswer({ data }, userAnswer) {
  const raw = parseRawFraction(userAnswer?.raw);
  if (!raw) return false;
  return raw.numerator === data.result.numerator && raw.denominator === data.result.denominator;
}

export function renderAnswer({ data }) {
  return <MathText math={answerLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  const parsed = parseRawFraction(userAnswer.raw);
  return <MathText math={parsed ? answerLatex(parsed) : userAnswer.raw} />;
}

function extensionLine(term, commonDenominator) {
  const factor = commonDenominator / term.fraction.denominator;
  return {
    original: term.fraction,
    factor,
    expanded: { numerator: term.fraction.numerator * factor, denominator: commonDenominator },
  };
}

function mixedConversionLine(term) {
  if (term.fraction.display !== "mixed") return null;
  const mixed = fractionToMixed(term.fraction);
  const sign = mixed.whole < 0 ? "-" : "";
  const numerator = Math.abs(mixed.whole) * mixed.denominator + mixed.numerator;
  return `${displayFractionLatex(term.fraction)}=${sign}\\frac{${Math.abs(mixed.whole)}\\cdot ${mixed.denominator}+${mixed.numerator}}{${mixed.denominator}}=${fractionValueLatex(
    term.fraction
  )}`;
}

function expandedExpressionLatex(terms, commonDenominator) {
  return terms
    .map((term, index) => {
      const expanded = extensionLine(term, commonDenominator).expanded;
      const body = fractionValueLatex(expanded);
      if (index === 0) return body;
      return `${term.op === "+" ? "+" : "-"}${body}`;
    })
    .join("");
}

function numeratorExpressionLatex(terms, commonDenominator) {
  return terms
    .map((term, index) => {
      const expanded = extensionLine(term, commonDenominator).expanded;
      const body = `${expanded.numerator}`;
      if (index === 0) return body;
      return `${term.op === "+" ? "+" : "-"}${body}`;
    })
    .join("");
}

export function renderExplanation({ data }) {
  const denominators = data.terms.map((term) => term.fraction.denominator);
  const commonDenominator = lcmMany(denominators);
  const expandedTerms = data.terms.map((term) => extensionLine(term, commonDenominator));
  const numeratorValue = data.result.numerator * (commonDenominator / data.result.denominator);
  const mixedConversions = data.terms.map(mixedConversionLine).filter(Boolean);

  return (
    <div>
      {mixedConversions.length > 0 && (
        <>
          <p>Najpierw zamieniamy liczby mieszane na ułamki niewłaściwe.</p>
          {mixedConversions.map((line, index) => (
            <p key={`mixed-${index}`}>
              <MathText math={line} />
            </p>
          ))}
        </>
      )}
      {commonDenominator === denominators[0] && denominators.every((denominator) => denominator === commonDenominator) ? (
        <p>Mianowniki są takie same, więc dodajemy albo odejmujemy liczniki, a mianownik zostaje bez zmian.</p>
      ) : (
        <>
          <p>Sprowadzamy ułamki do wspólnego mianownika.</p>
          {expandedTerms.map((item, index) => (
            <p key={index}>
              <MathText
                math={`${fractionValueLatex(item.original)}=${fractionLatex(
                  `${item.original.numerator}\\cdot ${item.factor}`,
                  `${item.original.denominator}\\cdot ${item.factor}`
                )}=${fractionValueLatex(item.expanded)}`}
              />
            </p>
          ))}
        </>
      )}
      <p>
        <MathText
          math={`${expressionLatex(data.terms, false)}=${expandedExpressionLatex(
            data.terms,
            commonDenominator
          )}=\\frac{${numeratorExpressionLatex(data.terms, commonDenominator)}}{${commonDenominator}}=\\frac{${numeratorValue}}{${commonDenominator}}`}
        />
      </p>
      {data.result.denominator !== commonDenominator && (
        <p>
          Skracamy wynik i otrzymujemy <MathText math={fractionValueLatex(data.result)} />.
        </p>
      )}
      {Math.abs(data.result.numerator) > data.result.denominator && data.result.denominator !== 1 && (
        <p>
          Ułamek niewłaściwy zapisujemy jako liczbę mieszaną:{" "}
          <MathText math={`${fractionValueLatex(data.result)}=${mixedLatex(fractionToMixed(data.result))}`} />.
        </p>
      )}
      {data.result.numerator < 0 && (
        <p>Wynik jest ujemny, bo odejmowany ułamek był większy od ułamka, od którego odejmowaliśmy.</p>
      )}
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  FractionAddSameDenominatorTask: taskAdapter,
  FractionSubtractSameDenominatorTask: taskAdapter,
  FractionAddRelatedDenominatorTask: taskAdapter,
  FractionSubtractRelatedDenominatorTask: taskAdapter,
  FractionAddCommonDenominatorTask: taskAdapter,
  FractionSubtractCommonDenominatorTask: taskAdapter,
  FractionThreeTermsTask: taskAdapter,
  FractionMixedThreeTermsTask: taskAdapter,
  FractionNegativeResultTask: taskAdapter,
  FractionMixedPlusProperTask: taskAdapter,
  FractionMixedMinusProperTask: taskAdapter,
  FractionMixedPlusMixedTask: taskAdapter,
  FractionMixedMinusMixedTask: taskAdapter,
  FractionMixedThreeExpressionTask: taskAdapter,
};
